import type { AnalyzerFunction } from './types';
import {
    AstNodeType,
    LogicalOperator,
    isAbsolutePointInTime,
    isRelativePointInTime,
    isSlidingWindow,
    isTimeframe,
    iterateAst,
    recurseParen,
} from '@thinkalpha/language-services';
import type { KnownAstInfo, KnownAstNode, Timeframe } from '@thinkalpha/language-services';
import { isDictionaryFunctionDef } from 'src/lib/dictionaryModel';
import type { DictionaryFunctionDef } from 'src/lib/dictionaryModel';

export const BACKTEST_ANALYZER_SOURCE = 'backtest-analyzer';

const BOOLEAN_LITERAL_MESSAGE = 'Boolean literals (`true` / `false`) are not supported in backtests.';
const NULL_LITERAL_MESSAGE = 'Null literals are not supported in backtests.';
const OBJECT_LITERAL_MESSAGE =
    'Bare interval/point-in-time literals (e.g. `1d`, `[10]`, `[2024-01-01T00:00:00Z]`) are not supported in backtests outside of an indicator argument.';

const buildBinaryOpMessage = (operator: string): string =>
    'Operator `' +
    operator +
    '` is not supported in backtests. Only comparison (>, <, >=, <=, ==, !=) and logical (AND, OR) operators are supported.';
const LOGICAL_NOT_MESSAGE = 'Logical NOT (`!`) is not supported in backtests.';
const UNARY_NEGATION_MESSAGE = 'Unary negation (`-`) is only supported on numeric literals in backtests.';
const buildUnaryUnsupportedMessage = (operator: string): string =>
    'Unary operator `' + operator + '` is not supported in backtests.';
const TERNARY_MESSAGE = 'Ternary (`?:`) expressions are not supported in backtests.';
const MATCH_MESSAGE = 'Match expressions are not supported in backtests.';
const MATCH_DEFAULT_NOT_LAST_MESSAGE = 'The default case of a match expression must come last.';

const buildUnsupportedIndicatorMessage = (functionName: string): string =>
    '`' + functionName + '` is not supported in backtests.';

const buildBarIntervalMessage = (functionName: string, index: number, paramName: string | undefined): string =>
    'Expected a bar interval like `1d` for `' +
    functionName +
    '` argument ' +
    String(index) +
    (paramName ? ' (`' + paramName + '`)' : '') +
    '.';
const buildNamedParamRelativePointInTimeMessage = (functionName: string, paramName: string): string =>
    'Relative point-in-time references (e.g. `[.20d]`) are not supported as the `' +
    paramName +
    '` named parameter of `' +
    functionName +
    '`.';
const buildNamedParamInstantTimeframeMessage = (functionName: string, paramName: string): string =>
    'Timeframes with an absolute-instant start (e.g. `[2024-01-01T00:00:00Z|0]`) are not supported as the `' +
    paramName +
    '` named parameter of `' +
    functionName +
    '`. Use a numeric bar count instead.';
const buildNamedParamAbsolutePointInTimeMessage = (functionName: string, paramName: string): string =>
    'Absolute point-in-time references (e.g. `[2024-01-01T00:00:00Z]`) are not supported as the `' +
    paramName +
    '` named parameter of `' +
    functionName +
    '`. Use a numeric bar count instead.';
const buildSilentDropMessage = (functionName: string, index: number, paramName: string | undefined): string =>
    '`' +
    functionName +
    '` argument in position ' +
    String(index) +
    (paramName ? ' (`' + paramName + '`)' : '') +
    ' cannot be expressed in backtest mode — this indicator does not have a backtester mapping for that parameter.';
const buildMissingRequiredArgMessage = (functionName: string, paramName: string | undefined, index: number): string =>
    '`' +
    functionName +
    '` is missing required argument' +
    (paramName ? ' `' + paramName + '`' : '') +
    ' at position ' +
    String(index) +
    '.';
const buildSurplusArgMessage = (functionName: string, index: number): string =>
    '`' + functionName + '` does not accept an argument at position ' + String(index) + '.';

const SUPPORTED_COMPARISON_OPERATORS = new Set<string>(['>', '<', '>=', '<=', '==', '!=']);

function isLogicalOperator(operator: string): boolean {
    return Object.prototype.hasOwnProperty.call(LogicalOperator, operator);
}

/**
 * Returns true if the const node sits as a direct argument of a call (possibly through paren
 * wrappers). Those positions are slot-classified by the call branch and would emit a duplicate
 * error here.
 */
function isInsideCallArgument(node: KnownAstInfo): boolean {
    let cursor: KnownAstInfo | undefined = node.parent;
    while (cursor?.type === AstNodeType.paren) {
        cursor = cursor.parent;
    }
    return cursor?.type === AstNodeType.call;
}

function isBacktestSupportedIndicator(fd: DictionaryFunctionDef): boolean {
    const imp = fd.indicator;
    if (imp.backtestData !== undefined) return true;
    if (imp.presets?.some((p) => p.backtestData !== undefined)) return true;
    return false;
}

type SlotKind =
    | { kind: 'bar-interval'; paramName: string | undefined }
    | { kind: 'expression' }
    | { kind: 'named-param'; paramName: string }
    | { kind: 'silent-drop'; paramName: string | undefined };

type ValueShape =
    | 'sliding-window'
    | 'count-point-in-time'
    | 'numeric-start-timeframe'
    | 'relative-point-in-time'
    | 'relative-point-in-time-timeframe'
    | 'instant'
    | 'instant-start-timeframe'
    | 'string'
    | 'other';

interface ParamSlotMetadata {
    name?: string;
    bars?: boolean;
    optional?: boolean;
    backtestData?: { paramName?: string };
}

function classifySlot(arg: KnownAstNode, param: ParamSlotMetadata): SlotKind {
    if (param.bars) return { kind: 'bar-interval', paramName: param.name };

    // Unwrap parens before expression-kind check so a paren-wrapped temporal literal
    // (e.g. `([.20d])`) doesn't escape via the expression slot. classifyValueShape
    // also recurseParens, so the two remain consistent.
    const resolved = recurseParen(arg);
    const isExpression =
        resolved.type === AstNodeType.call ||
        resolved.type === AstNodeType.binaryOperation ||
        resolved.type === AstNodeType.unaryOperation;
    if (isExpression) return { kind: 'expression' };

    const paramName = param.backtestData?.paramName;
    if (paramName) return { kind: 'named-param', paramName };

    return { kind: 'silent-drop', paramName: param.name };
}

function classifyValueShape(arg: KnownAstNode): ValueShape {
    const resolved = recurseParen(arg);
    if (resolved.type !== AstNodeType.const) return 'other';

    const value = resolved.value;
    if (typeof value === 'string') return 'string';
    if (typeof value === 'number') return 'count-point-in-time';
    if (isSlidingWindow(value)) return 'sliding-window';
    if (isRelativePointInTime(value)) return 'relative-point-in-time';
    if (isAbsolutePointInTime(value)) return 'instant';
    if (isTimeframe(value)) {
        const tf = value as Timeframe;
        // A relative point-in-time on either side of the timeframe is rejected by
        // the backtest mapping — check both before falling through to start-side
        // classification, otherwise `[10|.0d]` would slip through as numeric-start.
        // Guard the end check against literal null (the parser emits `end: null` for
        // unspecified ends, and `isRelativePointInTime(null)` returns true).
        const endIsRelative = tf.end != null && isRelativePointInTime(tf.end);
        if (isRelativePointInTime(tf.start) || endIsRelative) {
            return 'relative-point-in-time-timeframe';
        }
        if (typeof tf.start === 'number') return 'numeric-start-timeframe';
        if (isAbsolutePointInTime(tf.start)) return 'instant-start-timeframe';
    }
    return 'other';
}

function evaluateSlot(slot: SlotKind, shape: ValueShape, functionName: string, index: number): string | null {
    switch (slot.kind) {
        case 'expression':
            return null;
        case 'bar-interval':
            return shape === 'sliding-window' ? null : buildBarIntervalMessage(functionName, index, slot.paramName);
        case 'named-param':
            if (shape === 'relative-point-in-time' || shape === 'relative-point-in-time-timeframe') {
                return buildNamedParamRelativePointInTimeMessage(functionName, slot.paramName);
            }
            if (shape === 'instant-start-timeframe') {
                return buildNamedParamInstantTimeframeMessage(functionName, slot.paramName);
            }
            if (shape === 'instant') {
                return buildNamedParamAbsolutePointInTimeMessage(functionName, slot.paramName);
            }
            return null;
        case 'silent-drop':
            return buildSilentDropMessage(functionName, index, slot.paramName);
    }
}

function pushError(node: KnownAstNode, text: string): void {
    node.valid = false;
    node.errors.push({
        range: node.ranges?.node ?? { start: 0, end: 0 },
        text,
        source: BACKTEST_ANALYZER_SOURCE,
    });
}

/**
 * Flags formula shapes the backtest compiler cannot render. Walks the AST once and dispatches
 * by node type. Must run AFTER the dictionary analyzer so call nodes' `functionDef` is populated;
 * calls with unresolved `functionDef` are skipped (an unknown name is surfaced upstream).
 *
 * For the list of `BacktestCompilationErrorCode`s intentionally not surfaced, see the comment
 * on `backtestAnalyzer` in `./index.ts`.
 */
export const backtestAnalyzer: AnalyzerFunction = (root) => {
    for (const node of iterateAst(root)) {
        switch (node.type) {
            case AstNodeType.binaryOperation: {
                const { operator } = node;
                if (isLogicalOperator(operator)) break;
                if (SUPPORTED_COMPARISON_OPERATORS.has(operator)) break;
                pushError(node, buildBinaryOpMessage(operator));
                break;
            }
            case AstNodeType.unaryOperation: {
                const { operator, operand } = node;
                if (operator === '+') break;
                if (operator === '-') {
                    if (operand.type === AstNodeType.const && typeof operand.value === 'number') break;
                    pushError(node, UNARY_NEGATION_MESSAGE);
                    break;
                }
                if (operator === '!') {
                    pushError(node, LOGICAL_NOT_MESSAGE);
                    break;
                }
                pushError(node, buildUnaryUnsupportedMessage(operator));
                break;
            }
            case AstNodeType.ternary: {
                pushError(node, TERNARY_MESSAGE);
                break;
            }
            case AstNodeType.match: {
                pushError(node, MATCH_MESSAGE);
                const defaultIndex = node.cases.findIndex((c) => c.condition == null);
                if (defaultIndex !== -1 && defaultIndex !== node.cases.length - 1) {
                    pushError(node, MATCH_DEFAULT_NOT_LAST_MESSAGE);
                }
                break;
            }
            case AstNodeType.const: {
                if (isInsideCallArgument(node)) break;
                const { value } = node;
                if (typeof value === 'boolean') pushError(node, BOOLEAN_LITERAL_MESSAGE);
                else if (value === null) pushError(node, NULL_LITERAL_MESSAGE);
                else if (typeof value === 'object') pushError(node, OBJECT_LITERAL_MESSAGE);
                break;
            }
            case AstNodeType.call: {
                const fd = node.functionDef;
                if (!fd || !isDictionaryFunctionDef(fd)) break;

                if (!isBacktestSupportedIndicator(fd)) {
                    pushError(node, buildUnsupportedIndicatorMessage(node.source));
                }

                const params = fd.indicator.params ?? [];
                const len = Math.max(params.length, node.arguments.length);
                for (let i = 0; i < len; i++) {
                    const arg = node.arguments[i];
                    const param = params[i];
                    if (arg == null) {
                        if (param && param.optional !== true) {
                            pushError(node, buildMissingRequiredArgMessage(node.source, param.name, i));
                        }
                        continue;
                    }
                    if (!param) {
                        pushError(arg, buildSurplusArgMessage(node.source, i));
                        continue;
                    }
                    const slot = classifySlot(arg, param);
                    const shape = classifyValueShape(arg);
                    const message = evaluateSlot(slot, shape, node.source, i);
                    if (message != null) pushError(arg, message);
                }
                break;
            }
        }
    }
    return root;
};
